// Admin — shared "Purger le spam" widget. Two-step interaction:
//   1. Click → fetch preview → show modal with the matched count and
//      the first few rows so the admin can eyeball for false positives.
//   2. Confirm → POST /purge → refresh the parent list.
//
// Reused on both AdminNewsletterPage and AdminContactMessagesPage.
// The `scope` prop drives which pair of endpoints to hit.
import { useState } from "react";
import { ShieldAlert, Loader2, X } from "lucide-react";
import { adminApi } from "@/lib/adminApi";

const CFG = {
  newsletter: {
    label: "Purger le spam",
    title: "Purger les abonnés spam",
    previewFn: () => adminApi.spamNewsletterPreview(),
    purgeFn: () => adminApi.spamNewsletterPurge(),
    columns: ["email", "lang", "source", "reason"],
  },
  contact: {
    label: "Purger le spam",
    title: "Purger les messages spam",
    previewFn: () => adminApi.spamContactPreview(),
    purgeFn: () => adminApi.spamContactPurge(),
    columns: ["name", "email", "subject", "reason"],
  },
};

const SpamPurgeButton = ({ scope, onDone }) => {
  const cfg = CFG[scope];
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [purging, setPurging] = useState(false);

  const openModal = async () => {
    setOpen(true); setLoading(true); setError(null); setPreview(null);
    try {
      const p = await cfg.previewFn();
      setPreview(p);
    } catch (e) { setError(e.message || String(e)); }
    finally { setLoading(false); }
  };

  const doPurge = async () => {
    setPurging(true); setError(null);
    try {
      const r = await cfg.purgeFn();
      setPreview({ ...preview, deleted: r.deleted });
      // Wait a beat so the admin sees the confirmation, then close.
      setTimeout(() => {
        setOpen(false);
        setPurging(false);
        onDone && onDone();
      }, 900);
    } catch (e) {
      setError(e.message || String(e));
      setPurging(false);
    }
  };

  return (
    <>
      <button
        onClick={openModal}
        className="btn-ghost text-xs text-red-700 border-red-200 hover:bg-red-50"
        data-testid={`spam-purge-open-${scope}`}
      >
        <ShieldAlert size={13} /> {cfg.label}
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid={`spam-purge-modal-${scope}`}>
          <div className="bg-white rounded-[5px] max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[color:var(--gv-line)]">
              <div className="font-display text-lg text-[color:var(--gv-ink)]">{cfg.title}</div>
              <button onClick={() => setOpen(false)} className="text-[color:var(--gv-ink)]/50 hover:text-[color:var(--gv-ink)]" data-testid="spam-purge-close">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 overflow-y-auto flex-1">
              {loading ? (
                <div className="text-center py-10"><Loader2 size={22} className="animate-spin inline text-[color:var(--gv-pink)]" /></div>
              ) : error ? (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-[5px]">{error}</div>
              ) : preview ? (
                <>
                  {preview.deleted !== undefined ? (
                    <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-[5px] text-center" data-testid="spam-purge-result">
                      <strong>{preview.deleted}</strong> entrée{preview.deleted > 1 ? "s" : ""} supprimée{preview.deleted > 1 ? "s" : ""}.
                    </div>
                  ) : (
                    <>
                      <div className="text-[13px] text-[color:var(--gv-ink)]/75 mb-3">
                        Scannées : <strong>{preview.scanned}</strong> · Détectées comme spam :
                        <strong className="text-red-700 ml-1">{preview.matched_total}</strong>
                      </div>
                      {preview.matched_total > 0 ? (
                        <div className="border border-[color:var(--gv-line)] rounded-[5px] overflow-hidden">
                          <table className="w-full text-[12.5px]">
                            <thead className="bg-[color:var(--gv-mist)] text-left text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55">
                              <tr>{cfg.columns.map((c) => <th key={c} className="px-3 py-2">{c}</th>)}</tr>
                            </thead>
                            <tbody>
                              {(preview.samples || []).map((s, idx) => (
                                <tr key={idx} className="border-t border-[color:var(--gv-line)]">
                                  {cfg.columns.map((c) => (
                                    <td key={c} className="px-3 py-1.5 truncate max-w-xs">{s[c] || "—"}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {preview.matched_total > (preview.samples?.length || 0) && (
                            <div className="text-center text-[11.5px] text-[color:var(--gv-ink)]/55 py-2 border-t border-[color:var(--gv-line)]">
                              … et {preview.matched_total - preview.samples.length} de plus
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-[color:var(--gv-ink)]/55">Aucun spam détecté 🎉</div>
                      )}
                    </>
                  )}
                </>
              ) : null}
            </div>
            {preview && !preview.deleted && preview.matched_total > 0 && (
              <div className="px-5 py-3 border-t border-[color:var(--gv-line)] flex justify-end gap-2 bg-[color:var(--gv-mist)]/50">
                <button onClick={() => setOpen(false)} className="btn-ghost" data-testid="spam-purge-cancel">Annuler</button>
                <button
                  onClick={doPurge}
                  disabled={purging}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[5px] bg-red-600 text-white text-[13px] hover:bg-red-700 disabled:opacity-50"
                  data-testid="spam-purge-confirm"
                >
                  {purging ? <><Loader2 size={13} className="animate-spin" /> Suppression…</> : <><ShieldAlert size={13} /> Supprimer les {preview.matched_total} entrées</>}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SpamPurgeButton;
